import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allManga, categories, categoryKeys } from '@/mocks/manga';

export default function Category() {
  const { category } = useParams<{ category: string }>();
  const [activeCategory, setActiveCategory] = useState(category || '韩漫推荐');

  useEffect(() => {
    if (category) {
      setActiveCategory(category);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [category]);

  const currentKey = categoryKeys[activeCategory] || '韩漫';
  const mangas = allManga.filter((m) => m.category === currentKey);

  const categoryNames: Record<string, string> = {
    '韩漫推荐': '韩漫',
    '日漫大全': '日漫',
    '国漫排行榜': '国漫',
  };

  const categoryDescs: Record<string, string> = {
    '韩漫推荐': '精选韩国漫画推荐，涵盖恋爱、奇幻、热血、校园等多种题材，每日更新最新韩漫资讯。',
    '日漫大全': '日本漫画全集收录，从经典名作到热门新番，日漫爱好者的必逛频道。',
    '国漫排行榜': '国产漫画排行榜，一人之下、狐妖小红娘、斗罗大陆等热门国漫一榜打尽。',
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `土豪漫画网 - ${activeCategory}`,
    url: `https://tuhaomanhua.com/category/${activeCategory}`,
    description: categoryDescs[activeCategory] || '漫画分类页面',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16 md:pt-18">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: activeCategory },
              ]}
            />

            {/* Category Header */}
            <section className="w-full px-4 md:px-6 pt-6 pb-10 md:pb-14">
              <div className="text-center">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  {activeCategory}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto mb-8">
                  {categoryDescs[activeCategory]}共收录 <strong>{mangas.length}</strong> 部{categoryNames[activeCategory] || ''}漫画作品。
                </p>

                {/* Category Tabs */}
                <div className="inline-flex bg-background-100 rounded-full p-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${cat}`}
                      className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        activeCategory === cat
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
            </section>

            {/* Manga Grid */}
            <section className="w-full px-4 md:px-6 pb-16 md:pb-20">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {mangas.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={manga.coverImage}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm truncate group-hover:text-primary-500 transition-colors">
                        {manga.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 truncate">{manga.author}</p>
                      <p className="text-xs text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
                        {manga.description}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span className="text-xs font-semibold text-foreground-800">{manga.rating}</span>
                        </div>
                        <span className={`text-xs font-medium ${manga.status === '连载中' ? 'text-green-600' : 'text-foreground-500'}`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2.5">
                        {manga.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {mangas.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-foreground-500">暂无该分类的漫画</p>
                </div>
              )}
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}